/**
 * Shared helpers for session row/card.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';
import { getReportBySession } from '@/services/remote/reports.service';
import { reportKeys } from '@/hooks/useReportsRemote';
import type { ReportDetail } from '@/types/remote/report.types';
import type { SessionStatus } from '@/types/inperson/session.types';
import type { ListItem, QueryParams, SessionTab } from '@/types/remote/session.types';
import {
  REPORT_ELIGIBLE_STATUSES,
  REMOTE_ANIMATOR_SESSION_TYPES,
  REMOTE_FACILITATOR_SESSION_TYPES,
  SESSION_REMOTE_TYPE_FILTER_KEYS,
  VALIDATED_REPORT_STATUSES,
  type SessionRemoteFilters,
} from '@/components/sessions/session-configs';
import { expandNotCompletedSessionStatuses } from '@/utils/common-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { roleKind } from '@/utils/role-label-utils';
import { resolveListDateRange } from '@/utils/list-date-range-utils';
export { getEffectiveStatusConfig, getRemoteStatusKeysForTypes } from '@/utils/common-utils';

/** RBAC actions available on a session row/card. */
export interface SessionItemPageActions {
  canEditRow?: boolean;
  canCancelRow?: boolean;
  canExportCsv?: boolean;
  /** Per-type report permissions — derived from reports-remote RBAC. */
  canViewReportTutoring?: boolean;
  canViewReportInformationMeeting?: boolean;
  canViewReportWelcome?: boolean;
  canViewReportFieldEvent?: boolean;
  canJoinWelcomeMeeting?: boolean;
  canJoinInformationMeeting?: boolean;
}

/** Report presence / validated / disabled flags for a session row. */
export interface SessionReportState {
  hasReport: boolean;
  isReportValidated: boolean;
  reportDisabled: boolean;
}

/** Animator/facilitator row used when resolving remote session hosts. */
export interface SessionFacilitatorParticipant {
  id: string;
  userId?: string;
  role: string;
  attendanceStatus?: string;
  user?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    displayName?: string;
  };
}

/** Session fields needed to resolve animators (`facilitators` vs `participants`). */
interface SessionForFacilitatorLookup {
  type: string;
  facilitators?: SessionFacilitatorParticipant[];
  participants?: SessionFacilitatorParticipant[];
}

/** One participant from a remote session list item. */
type Participant = NonNullable<ListItem['participants']>[number];

/** Role-split participants used by {@link SessionRow} / {@link SessionCard}. */
export interface DerivedParticipants {
  tutor: Participant | undefined;
  students: Participant[];
  parents: Participant[];
  isInformationWelcomeOrWorkshop: boolean;
  animators: SessionFacilitatorParticipant[];
  volunteers: Participant[];
  welcomeParticipants: Participant[];
}

/** Join is allowed only for in-progress / to-launch welcome or information meetings. */
export function canShowJoinButton(
  session: { status?: string; type?: string },
  pageActions?: SessionItemPageActions,
): boolean {
  if (session.status !== 'IN_PROGRESS' && session.status !== 'TO_LAUNCH') return false;
  if (session.type === 'welcome_meeting') return pageActions?.canJoinWelcomeMeeting ?? false;
  if (session.type === 'information_meeting')
    return pageActions?.canJoinInformationMeeting ?? false;
  return false;
}

/** Whether the current user can view/write the report for this session type. */
export const getCanViewReport = (
  sessionType: ListItem['type'],
  pageActions: SessionItemPageActions | undefined,
): boolean => {
  if (sessionType === 'individual_tutoring' || sessionType === 'group_tutoring')
    return pageActions?.canViewReportTutoring ?? false;
  if (sessionType === 'information_meeting')
    return pageActions?.canViewReportInformationMeeting ?? false;
  if (sessionType === 'welcome_meeting') return pageActions?.canViewReportWelcome ?? false;
  if (sessionType === 'workshop') return pageActions?.canViewReportFieldEvent ?? false;
  return false;
};

/** Converts HH:MM:SS (or HH:MM) to HHhMM. */
export const formatTime = (time: string): string => {
  const parts = time.split(':');
  if (parts.length >= 2) return `${parts[0]}h${parts[1]}`;
  return time;
};

/** Duration in minutes between two `HH:MM` times; null when end is missing or inverted. */
export const calcDurationMinutes = (start: string, end?: string | null): number | null => {
  if (!end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = eh * 60 + em - (sh * 60 + sm);
  return diff > 0 ? diff : null;
};

/** Parse `YYYY-MM-DD` as a local date (avoids UTC midnight shift). */
export const parseSessionDate = (raw: string): Date => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Date(raw);
};

/** True for completed / cancelled / absent terminal statuses. */
export const isSessionTerminated = (status: SessionStatus): boolean =>
  status === 'COMPLETED' ||
  status === 'CLOSED' ||
  status === 'CANCELLED' ||
  status === 'NOT_COMPLETED' ||
  status === 'PARTICIPANTS_ABSENT' ||
  status === 'BOTH_ABSENT' ||
  status === 'TUTOR_ABSENT' ||
  status === 'STUDENT_ABSENT';

/** Workshop uses `compteRenduId`; others also allow eligible session statuses. */
export const getSessionReportState = (
  session: ListItem,
  isLoadingReport: boolean,
): SessionReportState => {
  const hasReport =
    session.type === 'workshop'
      ? !!session.compteRenduId
      : REPORT_ELIGIBLE_STATUSES.has(session.status) || !!session.compteRenduId;

  const isReportValidated =
    hasReport && VALIDATED_REPORT_STATUSES.has(session.compteRenduStatus ?? '');

  const reportDisabled = isLoadingReport || !hasReport || session.status === 'BOTH_ABSENT';

  return { hasReport, isReportValidated, reportDisabled };
};

/** Resolves animators for a remote session (server `facilitators`, else participants). */
export function getSessionFacilitators<T extends SessionForFacilitatorLookup>(
  session: T,
): SessionFacilitatorParticipant[] {
  if (REMOTE_FACILITATOR_SESSION_TYPES.has(session.type) && session.facilitators) {
    return session.facilitators;
  }
  if (!REMOTE_ANIMATOR_SESSION_TYPES.has(session.type)) {
    return [];
  }
  return (
    session.participants?.filter((p) => {
      const kind = roleKind(p.role);
      return kind === 'recruiter' || kind === 'coordinator' || kind === 'volunteer';
    }) ?? []
  );
}

/** Split tutors / students / parents / animators / volunteers by session type. */
export const deriveSessionParticipants = (session: ListItem): DerivedParticipants => {
  const participants = session.participants ?? [];

  const tutor = participants.find((p) => roleKind(p.role) === 'tutor');
  const students = participants.filter((p) => roleKind(p.role) === 'student');
  const parents = participants.filter((p) => roleKind(p.role) === 'parent');

  const isInformationWelcomeOrWorkshop = [
    'information_meeting',
    'welcome_meeting',
    'workshop',
  ].includes(session.type);

  const animators = getSessionFacilitators(session);

  // For information meetings, tutors are the participants; for other session types, volunteers are a separate role.
  let volunteers: Participant[];
  if (!isInformationWelcomeOrWorkshop) {
    volunteers = participants.filter((p) => p.role === 'volunteer');
  } else if (session.type === 'information_meeting') {
    volunteers = participants.filter((p) => roleKind(p.role) === 'tutor');
  } else {
    volunteers = [];
  }

  const welcomeParticipants = [...students, ...parents];

  return {
    tutor,
    students,
    parents,
    isInformationWelcomeOrWorkshop,
    animators,
    volunteers,
    welcomeParticipants,
  };
};

/** Edit-button tooltip; explains why edit is locked on terminal statuses. */
export const getEditTooltip = (isDisabled: boolean, status: SessionStatus): string => {
  if (!isDisabled) return 'Modifier la séance';
  if (status === 'COMPLETED') return getErrorMessage('cannotEditSessionEnded');
  if (status === 'NOT_COMPLETED' || status === 'PARTICIPANTS_ABSENT')
    return getErrorMessage('cannotEditSessionNotHeld');
  if (status === 'CANCELLED') return getErrorMessage('cannotEditSessionCancelled');
  if (status === 'CLOSED') return getErrorMessage('cannotEditSessionClosed');
  if (status === 'BOTH_ABSENT') return getErrorMessage('cannotEditPairAbsent');
  if (status === 'TUTOR_ABSENT') return getErrorMessage('cannotEditTutorAbsent');
  if (status === 'STUDENT_ABSENT') return getErrorMessage('cannotEditStudentAbsent');
  return getErrorMessage('cannotEdit');
};

/** Cancel-button tooltip; explains why cancel is locked. */
export const getCancelTooltip = (isDisabled: boolean, status: SessionStatus): string => {
  if (!isDisabled) return 'Annuler la séance';
  if (status === 'CANCELLED') return getErrorMessage('cannotCancelAlreadyCancelled');
  if (status === 'CLOSED') return getErrorMessage('cannotCancelSessionClosed');
  if (status === 'COMPLETED') return getErrorMessage('cannotCancelSessionEnded');
  if (status === 'NOT_COMPLETED' || status === 'PARTICIPANTS_ABSENT')
    return getErrorMessage('cannotCancelSessionNotHeld');
  if (status === 'BOTH_ABSENT') return getErrorMessage('cannotCancelPairAbsent');
  if (status === 'TUTOR_ABSENT') return getErrorMessage('cannotCancelTutorAbsent');
  if (status === 'STUDENT_ABSENT') return getErrorMessage('cannotCancelStudentAbsent');
  return getErrorMessage('cannotCancel');
};

/** Report-button tooltip (loading / missing / validated / draft). */
export const getReportTooltip = (
  isLoading: boolean,
  isValidated: boolean,
  reportStatus: string | undefined,
  hasReport: boolean,
  sessionStatus: SessionStatus,
): string => {
  if (isLoading) return 'Chargement…';
  if (sessionStatus === 'BOTH_ABSENT') return getErrorMessage('reportUnavailablePairAbsent');
  if (!hasReport) {
    if (sessionStatus === 'PLANNED' || sessionStatus === 'TO_LAUNCH')
      return getErrorMessage('reportUnavailableNotStarted');
    if (sessionStatus === 'IN_PROGRESS') return getErrorMessage('reportUnavailableInProgress');
    if (sessionStatus === 'CANCELLED') return getErrorMessage('reportUnavailableCancelled');
    return getErrorMessage('reportUnavailable');
  }
  if (isValidated) return 'Voir le compte rendu validé';
  if (reportStatus === 'saisi') return 'Modifier le compte rendu';
  return 'Rédiger le compte rendu';
};

/** Central error copy from {@link getErrorMessage}. */
export function getRemoteTableApiError(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** Open the remote `GET /comptes-rendus/:id` view for a session. */
export const useNavigateToRemoteReport = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = React.useState(false);

  const navigateToReport = React.useCallback(
    async (sessionId: string, sessionType?: string, reportId?: string | null) => {
      if (reportId) {
        void navigate('/reports', {
          state: {
            editReport: {
              sessionId,
              reportId,
              sessionType,
            },
          },
        });
        return;
      }

      setIsLoading(true);
      try {
        const data = await queryClient.fetchQuery({
          queryKey: reportKeys.bySession(sessionId),
          queryFn: () => getReportBySession(sessionId),
        });

        if (data == null) {
          toast.info(getErrorMessage('noReportForSession'));
          return;
        }

        const report = data as ReportDetail;
        if (!report.id) {
          toast.info(getErrorMessage('noReportForSession'));
          return;
        }

        void navigate('/reports', {
          state: {
            editReport: {
              sessionId,
              reportId: report.id,
              sessionType: report.session?.type ?? sessionType,
            },
          },
        });
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, queryClient],
  );

  return { navigateToReport, isLoading };
};

/** Resolve API `dateFrom`/`dateTo` from the period chip or custom dates. */
export function getDateRangeFromFilters(filters: SessionRemoteFilters): {
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
} {
  const { dateFrom, dateTo } = resolveListDateRange(filters, {
    periodEnd: 'endOfPeriod',
  });
  return { dateFrom, dateTo };
}

/** Returns the API session types that correspond to a given tab. */
export function getSessionTypesForTab(tab: SessionTab): string[] {
  switch (tab) {
    case 'tutoring':
      return ['individual_tutoring'];
    case 'information-meeting':
      return ['information_meeting'];
    case 'welcome-meeting':
      return ['welcome_meeting'];
    case 'field-event':
      return ['workshop'];
    default:
      return [];
  }
}

/** All-tab types: RBAC-allowed types, then the selected type chips (fallback tutoring). */
export function buildToutTypes(
  filterTypes: string[],
  allowedTabIds: SessionTab[] | undefined,
): string[] {
  const nonToutTabs = (allowedTabIds ?? []).filter((id) => id !== 'all');
  const allowedTypes =
    nonToutTabs.length > 0
      ? nonToutTabs.flatMap(getSessionTypesForTab)
      : [...SESSION_REMOTE_TYPE_FILTER_KEYS];
  const selected =
    filterTypes.length > 0
      ? filterTypes.filter((t) => allowedTypes.includes(t))
      : ['individual_tutoring'];
  const effective = selected.filter((t) => allowedTypes.includes(t));
  const fallbackTypes = allowedTypes.includes('individual_tutoring')
    ? ['individual_tutoring']
    : allowedTypes;
  return effective.length > 0 ? effective : fallbackTypes;
}

/** `undefined` = no filter; empty array is still sent (explicit empty managed-by). */
export function buildManagedByParam(
  managedByIds: SessionRemoteFilters['managedByIds'],
): string[] | undefined {
  if (managedByIds === undefined) return undefined;
  if (managedByIds === 'all') return ['all'];
  if (managedByIds === 'unmanaged') return ['unmanaged'];
  if (Array.isArray(managedByIds) && managedByIds.length > 0) return managedByIds;
  return undefined;
}

/** Tab types win; on `all` use the selected chips or tutoring. */
export function resolveRemoteSessionFilterTypes(
  activeTab: SessionTab,
  filterTypes: string[],
): string[] {
  const tabTypes = getSessionTypesForTab(activeTab);
  if (tabTypes.length > 0) return tabTypes;
  return filterTypes.length > 0 ? filterTypes : ['individual_tutoring'];
}

/** Map UI filters + tab to remote session list query params. */
export function buildRemoteSessionListParams(
  filters: SessionRemoteFilters,
  options: {
    page: number;
    pageSize: number;
    activeTab: SessionTab;
    allowedTabIds?: SessionTab[];
    /** Deep-link pin from pair → sessions; wins over the table tutor chip. */
    pinnedTutorUserId?: string;
    /** Deep-link pin from pair → sessions; wins over the table student chip. */
    pinnedStudentUserId?: string;
  },
): QueryParams {
  const { dateFrom, dateTo } = getDateRangeFromFilters(filters);
  const params: QueryParams = {
    page: options.page,
    pageSize: options.pageSize,
    sortBy: 'sessionDate',
    sortOrder: 'asc',
    sessionFormat: 'remote',
  };

  params.types =
    options.activeTab === 'all'
      ? buildToutTypes(filters.types, options.allowedTabIds)
      : getSessionTypesForTab(options.activeTab);

  if (filters.statuses.length > 0) {
    params.statuses = expandNotCompletedSessionStatuses(filters.statuses);
  }
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (filters.search) params.search = filters.search;
  const tutorUserId = options.pinnedTutorUserId ?? filters.tutorUserId;
  const studentUserId = options.pinnedStudentUserId ?? filters.studentUserId;
  if (tutorUserId) params.tutorUserId = tutorUserId;
  if (studentUserId) params.studentUserId = studentUserId;

  const managedBy = buildManagedByParam(filters.managedByIds);
  if (managedBy) params.managedByIds = managedBy;

  if (filters.programId) params.programId = filters.programId;
  if (filters.schoolYearId) params.schoolYearId = filters.schoolYearId;
  if (filters.recurrenceType) params.recurrenceType = filters.recurrenceType;

  return params;
}

/** True when any remote-session filter differs from the table defaults. */
export function hasRemoteSessionActiveFilters(
  filters: SessionRemoteFilters,
  defaults: SessionRemoteFilters,
): boolean {
  return (
    filters.statuses.length > 0 ||
    filters.dateRange !== defaults.dateRange ||
    !!filters.customDateFrom ||
    !!filters.customDateTo ||
    !!filters.search ||
    !!filters.tutorUserId ||
    !!filters.studentUserId ||
    !!filters.managedByIds ||
    !!filters.programId ||
    !!filters.schoolYearId ||
    !!filters.recurrenceType
  );
}
